// Контракт API пульта: «Кнопки и стадии сделок» — куда Анна передаёт сделку (Ruling 125).
//
// - `GET /api/v1/bitrix/targets` (любой вошедший) → `BitrixTargetsState`: три назначения из настроек и справочники для
//   выбора — воронки и стадии сделок Битрикса и кнопки квалификации Омнидеска. Справочники помнятся 10 минут (`at` — когда
//   прочитаны). Не прочитались — у справочника `status: 'unavailable'` с причиной и текстом, назначения отдаются всё равно;
//   без списка кнопок id кнопки вводится вручную;
// - `PUT /api/v1/bitrix/targets` (admin) — тело: назначения, которые меняются, каждое целиком (`buttonId`, `expect`,
//   необязательный `label`) → `{ targets }`. Сервер проверяет значения по правилам настроек A9 (`bad_settings`), стадию — в
//   Битриксе заново, не из памяти (есть ли она в выбранной воронке: иначе 400, Битрикс не ответил или вебхука нет — 502
//   `bitrix_unavailable`), а если список кнопок Омнидеска читается — что кнопка в нём есть (400). Подпись кнопки, если её не
//   прислали, берёт из Омнидеска; без кнопки подписи нет. Журнал — одна строка `settings.bitrix_targets`.
// - Передача в продажи (план A24, задача 4а): `GET` отдаёт ещё `salesHandover` (настройка `bitrix.salesHandover`) и
//   `salesStaff` — активных сотрудников отдела «Первые продажи» и есть ли у каждого пользователь Битрикса; `PUT
//   /api/v1/bitrix/sales-handover` (admin) — `{ mode, staffId }` → `{ salesHandover }`. Сервер сверяет сотрудника с отделом
//   и его пользователя Битрикса заново (иначе 400 с текстом `SALES_HANDOVER_ERRORS_RU`; база Омнидеска не ответила — 502
//   `omni_read_failed`), имя пишет сам. Журнал — одна строка `settings.sales_handover`.
//
// Назначения — копия `BITRIX_TARGET_KINDS` настроек A9, семантика стадий — копия справочника (`src/bitrix/funnels.ts`);
// совпадение держит `tests/console/contract.test.ts`, коды ошибок — `tests/console/bitrix-targets.test.ts`.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { MomentSchema } from './common.js';

// ───────────────────────────── назначения ─────────────────────────────

/** Назначения: звонок клиенту, в продажи, отказ (копия настроек A9). */
export const BITRIX_TARGET_KINDS = ['callRequest', 'toSales', 'reject'] as const;
export const BitrixTargetKindSchema = z.enum(BITRIX_TARGET_KINDS);
export type BitrixTargetKind = z.infer<typeof BitrixTargetKindSchema>;

/** Как назначения называются в пульте и когда Анна ими пользуется. */
export const BITRIX_TARGET_RU: Readonly<Record<BitrixTargetKind, { readonly titleRu: string; readonly whenRu: string }>> = {
  callRequest: {
    titleRu: 'Звонок клиенту',
    whenRu: 'Клиент оставил телефон или просит позвонить: сделка уходит на звонок квалификатору. Без кнопки — только задача квалификатору.',
  },
  toSales: { titleRu: 'В продажи', whenRu: 'Целевой клиент: сделка уходит менеджерам продаж.' },
  reject: { titleRu: 'Отказ', whenRu: 'Нецелевой клиент: после суток тишины сделка закрывается с причиной отказа.' },
};

/** Где сделка: воронка (id) и код стадии (`C8:LOSE`). */
export const BitrixStageRefSchema = z.object({ categoryId: z.number().int().nonnegative(), stageId: z.string().min(1) });
export type BitrixStageRef = z.infer<typeof BitrixStageRefSchema>;

/** Назначение из настроек: кнопка Омнидеска, стадия после неё, подпись кнопки; чего не выбрали — `null`. */
export const BitrixTargetSchema = z.object({
  buttonId: z.string().min(1).nullable(),
  expect: BitrixStageRefSchema.nullable(),
  label: z.string().min(1).nullable(),
});
export type BitrixTarget = z.infer<typeof BitrixTargetSchema>;

export const BitrixTargetsSchema = z.object({
  callRequest: BitrixTargetSchema,
  toSales: BitrixTargetSchema,
  reject: BitrixTargetSchema,
});
export type BitrixTargets = z.infer<typeof BitrixTargetsSchema>;

// ───────────────────────────── справочники ─────────────────────────────

/** Семантика стадии: в работе, успех, провал (копия справочника Битрикса). */
export const DEAL_STAGE_SEMANTICS = ['process', 'success', 'failure'] as const;
export const DealStageSemanticsSchema = z.enum(DEAL_STAGE_SEMANTICS);

export const BitrixStageSchema = z.object({ id: z.string().min(1), nameRu: z.string(), semantics: DealStageSemanticsSchema });
export type BitrixStage = z.infer<typeof BitrixStageSchema>;

/** Воронка сделок: id (основная — 0), название и стадии в порядке Битрикса. */
export const BitrixFunnelSchema = z.object({ id: z.number().int().nonnegative(), nameRu: z.string(), stages: z.array(BitrixStageSchema) });
export type BitrixFunnel = z.infer<typeof BitrixFunnelSchema>;

/** Вид кнопки Омнидеска: создать, перенести, найти и перенести; незнакомый вид — «другое». */
export const QUALIFY_BUTTON_ACTIONS = ['create', 'move', 'check_and_move', 'other'] as const;
export const QualifyButtonActionSchema = z.enum(QUALIFY_BUTTON_ACTIONS);

/**
 * Кнопка квалификации Омнидеска: куда переносит сделку (`moveTo`, только у «перенести»), где создаёт, если сделки нет
 * (`createIn`), и закрывает ли чат с причиной отказа (как «ЗИН»).
 */
export const QualifyButtonViewSchema = z.object({
  id: z.string().min(1),
  labelRu: z.string(),
  action: QualifyButtonActionSchema,
  moveTo: BitrixStageRefSchema.nullable(),
  createIn: BitrixStageRefSchema.nullable(),
  closesChat: z.boolean(),
});
export type QualifyButtonView = z.infer<typeof QualifyButtonViewSchema>;

/** Почему справочник не прочитан: сервис не видит ключ (вебхук Битрикса, токен Омнидеска) или сервис не ответил. */
export const DIRECTORY_UNAVAILABLE_REASONS = ['not_configured', 'failed'] as const;
export const DirectoryUnavailableReasonSchema = z.enum(DIRECTORY_UNAVAILABLE_REASONS);

/** Справочник: прочитан (`items` и момент чтения `at`) или нет (причина и текст для человека). */
function directorySchema<T extends z.ZodType>(item: T) {
  return z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), items: z.array(item), at: MomentSchema }),
    z.object({ status: z.literal('unavailable'), reason: DirectoryUnavailableReasonSchema, messageRu: z.string().min(1) }),
  ]);
}

export const FunnelsDirectorySchema = directorySchema(BitrixFunnelSchema);
export type FunnelsDirectory = z.infer<typeof FunnelsDirectorySchema>;

export const ButtonsDirectorySchema = directorySchema(QualifyButtonViewSchema);
export type ButtonsDirectory = z.infer<typeof ButtonsDirectorySchema>;

// ───────────────────────────── передача в продажи (план A24, задача 4а) ─────────────────────────────

/** Кому «в продажи» передаёт чат после «ЦА ОП» (копия `SALES_HANDOVER_MODES` настроек A9). */
export const SALES_HANDOVER_MODES = ['sales', 'duty'] as const;
export const SalesHandoverModeSchema = z.enum(SALES_HANDOVER_MODES);
export type SalesHandoverMode = z.infer<typeof SalesHandoverModeSchema>;

/** Режимы словами — как подписи настройки A9. */
export const SALES_HANDOVER_MODE_RU: Readonly<Record<SalesHandoverMode, string>> = {
  sales: 'руководителю продаж: чат в «Первые продажи»',
  duty: 'дежурному квалификатору, как раньше',
};

/** Передача в продажи из настроек: режим, сотрудник Омнидеска и его имя на момент выбора; не выбран — `null`. */
export const SalesHandoverSchema = z.object({
  mode: SalesHandoverModeSchema,
  staffId: z.string().min(1).nullable(),
  staffName: z.string().nullable(),
});
export type SalesHandover = z.infer<typeof SalesHandoverSchema>;

/** Сотрудник отдела «Первые продажи» для выбора: id, имя коротко («Виталий С.»), есть ли у него пользователь Битрикса. */
export const SalesStaffViewSchema = z.object({ id: z.string().min(1), nameRu: z.string(), bitrixUser: z.boolean() });
export type SalesStaffView = z.infer<typeof SalesStaffViewSchema>;

export const SalesStaffDirectorySchema = directorySchema(SalesStaffViewSchema);
export type SalesStaffDirectory = z.infer<typeof SalesStaffDirectorySchema>;

/**
 * `GET /api/v1/bitrix/targets`: назначения и справочники для выбора; передача в продажи (план A24) — настройка и сотрудники
 * отдела «Первые продажи» (читаются из базы Омнидеска при каждом запросе: не прочитались — `unavailable`).
 */
export const BitrixTargetsStateSchema = z.object({
  targets: BitrixTargetsSchema,
  funnels: FunnelsDirectorySchema,
  buttons: ButtonsDirectorySchema,
  salesHandover: SalesHandoverSchema,
  salesStaff: SalesStaffDirectorySchema,
});
export type BitrixTargetsState = z.infer<typeof BitrixTargetsStateSchema>;

/** Тексты справочников, которые не прочитались. */
export const BITRIX_DIRECTORY_RU = {
  funnelsNotConfigured:
    'Сервис не видит вебхук Битрикса (BITRIX_WEBHOOK_URL): воронки и стадии не прочитать. Задайте его командой `ssh -t omni ai-qual-env set BITRIX_WEBHOOK_URL`.',
  funnelsFailed: (reasonRu: string): string => `Воронки и стадии Битрикса не прочитаны: ${reasonRu}. Попробуйте через минуту.`,
  funnelsTimeout: (secondsRu: string): string => `Битрикс не ответил за ${secondsRu}: воронки и стадии не прочитаны. Попробуйте через минуту.`,
  buttonsNotConfigured:
    'Сервис не видит сервисный токен Анны (OMNI_SVC_TOKEN): список кнопок Омнидеска не прочитать — кнопку можно указать по id вручную.',
  buttonsAuth: (status: number): string =>
    `Омнидеск не принял сервисный токен Анны (код ${String(status)}): список кнопок не прочитан — кнопку можно указать по id вручную.`,
  buttonsFailed: (reasonRu: string): string => `Список кнопок Омнидеска не прочитан: ${reasonRu}. Кнопку можно указать по id вручную.`,
} as const;

// ───────────────────────────── изменение ─────────────────────────────

/** Что ждёт сервер в теле `PUT`, по полю. */
export const BITRIX_TARGETS_BODY_ERRORS_RU = {
  body: 'Тело запроса — JSON-объект с назначениями, которые меняются, например {"toSales": {"buttonId": "btn_7d3ju3y", "expect": {"categoryId": 0, "stageId": "PREPARATION_MIG"}}}.',
  empty: 'Нечего сохранять: передайте хотя бы одно назначение — callRequest, toSales или reject.',
  kind: 'Назначения — только callRequest, toSales и reject.',
  target: 'Назначение — объект с полями buttonId, expect и необязательным label, других полей нет.',
  buttonId: 'buttonId — id кнопки Омнидеска строкой или null, если кнопка не выбрана.',
  expect: 'expect — {"categoryId": <id воронки числом>, "stageId": "<код стадии>"} или null, если стадия не выбрана.',
  label: 'label — подпись кнопки строкой или null.',
} as const;

const E = BITRIX_TARGETS_BODY_ERRORS_RU;

/** Назначение в теле `PUT`: кнопка и стадия — обязательно (`null` — не выбрано), подпись — по желанию. */
export const BitrixTargetInputSchema = z.strictObject(
  {
    buttonId: z.string({ error: E.buttonId }).nullable(),
    expect: z
      .strictObject(
        {
          categoryId: z.number({ error: E.expect }).int(E.expect).nonnegative(E.expect),
          stageId: z.string({ error: E.expect }),
        },
        { error: E.expect },
      )
      .nullable(),
    label: z.string({ error: E.label }).nullable().optional(),
  },
  { error: E.target },
);
export type BitrixTargetInput = z.infer<typeof BitrixTargetInputSchema>;

/** Тело `PUT /api/v1/bitrix/targets`: назначения, которые меняются, хотя бы одно. */
export const BitrixTargetsPutBodySchema = z
  .strictObject(
    {
      callRequest: BitrixTargetInputSchema.optional(),
      toSales: BitrixTargetInputSchema.optional(),
      reject: BitrixTargetInputSchema.optional(),
    },
    { error: (issue) => (issue.code === 'unrecognized_keys' ? E.kind : E.body) },
  )
  .refine((body) => Object.values(body).some((target) => target !== undefined), { error: E.empty });
export type BitrixTargetsPutBody = z.infer<typeof BitrixTargetsPutBodySchema>;

/** Ответ `PUT`: назначения после записи. */
export const BitrixTargetsPutResponseSchema = z.object({ targets: BitrixTargetsSchema });
export type BitrixTargetsPutResponse = z.infer<typeof BitrixTargetsPutResponseSchema>;

/** Что ждёт сервер в теле `PUT /api/v1/bitrix/sales-handover`. */
export const SALES_HANDOVER_BODY_ERROR_RU =
  'Тело запроса — JSON-объект {"mode": "sales" или "duty", "staffId": "<id сотрудника Омнидеска>" или null}, других полей нет.';

/** Тело `PUT /api/v1/bitrix/sales-handover` (admin): режим и сотрудник «Первых продаж» (`null` — не выбран). */
export const SalesHandoverPutBodySchema = z.strictObject(
  { mode: SalesHandoverModeSchema, staffId: z.string().min(1).nullable() },
  { error: SALES_HANDOVER_BODY_ERROR_RU },
);
export type SalesHandoverPutBody = z.infer<typeof SalesHandoverPutBodySchema>;

/** Ответ `PUT /api/v1/bitrix/sales-handover`: передача в продажи после записи. */
export const SalesHandoverPutResponseSchema = z.object({ salesHandover: SalesHandoverSchema });
export type SalesHandoverPutResponse = z.infer<typeof SalesHandoverPutResponseSchema>;

/** Отказы `PUT /api/v1/bitrix/sales-handover` (400 `bad_request`); база Омнидеска не ответила — 502 `omni_read_failed`. */
export const SALES_HANDOVER_ERRORS_RU = {
  noStaff: (staffId: string): string => `Сотрудника ${staffId} нет среди активных сотрудников отдела «Первые продажи»: выберите из списка.`,
  noBitrixUser: (nameRu: string): string =>
    `У сотрудника ${nameRu} нет пользователя Битрикса: без него не поставить задачу и не передать сделку. Привяжите его в Омнидеске («Настройки → Битрикс24 → Пользователи») и выберите снова.`,
} as const;

// ───────────────────────────── ошибки ─────────────────────────────

/** Коды ошибок «Кнопок и стадий сделок» и их HTTP-статусы (копия `API_ERROR_STATUS`, `src/console/errors.ts`). */
export const BITRIX_TARGETS_ERROR_STATUS = {
  /** Тело не того вида; воронки, стадии или кнопки нет. */
  bad_request: 400,
  /** Значение не проходит правила настроек A9 (например, стадия не своей воронки). */
  bad_settings: 400,
  /** Менять может только admin. */
  forbidden: 403,
  /** Стадию не проверить: Битрикс не ответил или сервис не видит его вебхук — назначение не сохранено. */
  bitrix_unavailable: 502,
} as const;
export type BitrixTargetsErrorCode = keyof typeof BITRIX_TARGETS_ERROR_STATUS;

/** Тексты отказов `PUT` (у каждого — свой). */
export const BITRIX_TARGETS_ERRORS_RU = {
  noFunnel: (categoryId: number): string =>
    `В Битриксе нет воронки ${String(categoryId)} (или вебхуку Анны она не видна): выберите воронку из списка.`,
  noStage: (funnelRu: string, categoryId: number, stageId: string): string =>
    `В воронке «${funnelRu}» (${String(categoryId)}) нет стадии ${stageId}: выберите стадию из списка.`,
  noButton: (buttonId: string): string => `Кнопки «${buttonId}» нет в Омнидеске: выберите кнопку из списка.`,
  bitrixNotConfigured:
    'Сервис не видит вебхук Битрикса (BITRIX_WEBHOOK_URL), поэтому стадию не проверить — назначение не сохранено. Задайте вебхук командой `ssh -t omni ai-qual-env set BITRIX_WEBHOOK_URL`.',
  bitrixFailed: (reasonRu: string): string =>
    `Битрикс не ответил, поэтому стадию не проверить — назначение не сохранено. Попробуйте через минуту. Причина: ${reasonRu}`,
  bitrixTimeout: (secondsRu: string): string =>
    `Битрикс не ответил за ${secondsRu}, поэтому стадию не проверить — назначение не сохранено. Попробуйте через минуту.`,
} as const;
